import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building, Edit, Trash2 } from 'lucide-react';
import { useAuth, usePermissions } from '@contexts/AuthContext';
import { assessmentService, supplierService, noteService } from '@services';
import PageLayout from '@components/layout/PageLayout';
import DetailPageHeader from '@components/common/DetailPageHeader';
import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import EntityActivity from '@components/common/EntityActivity';
import GenericModal from '@components/common/modals/GenericModal';
import ConfirmationModal from '@components/common/modals/ConfirmationModal';
import SupplierInfo from '@components/features/suppliers/SupplierInfo';
import SupplierContracts from '@components/features/suppliers/SupplierContracts';
import SupplierAssessments from '@components/features/suppliers/SupplierAssessments';
import { AttachmentSection } from '@components/features/attachments';
import { EditSupplierForm } from '@components/features/suppliers/EditSupplierForm';
import { UpdateSupplierRequest, ROLES, ApiError } from '@app-types';
import { hasRole } from '@utils/permissionUtils';
import { showToast } from '@utils/toast';
import { getSupplierStatusLabel } from '@utils/referenceCatalog';
import { useTableSort } from '@hooks/useTableSort';
import { LoadingState } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import styles from './SupplierDetailPage.module.css';

// Contracts sub-list column key -> sortBy value the contracts API understands
const CONTRACT_SORT_MAPPING: Record<string, string> = {
  contract_number: 'contract_number',
  name: 'name',
  value: 'amount',
  start_date: 'start_date',
  end_date: 'end_date',
  status: 'status',
  scope: 'scope',
  division: 'division'
};

const SupplierDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const supplierId = Number.parseInt(id || '0', 10);

  // Modal state for editing supplier
  const [showEditModal, setShowEditModal] = useState(false);

  // Modal state for deleting supplier
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Pagination state for contracts
  const [contractsPage, setContractsPage] = useState(1);
  const [contractsPageSize, setContractsPageSize] = useState(25);
  const {
    sortConfig: contractsSortConfig,
    handleSort: handleContractsSort,
    isSorted: isContractsSorted
  } = useTableSort({
    columnMapping: CONTRACT_SORT_MAPPING,
    defaultSort: { field: 'contract_number', direction: 'ASC' },
    onPageReset: () => setContractsPage(1)
  });

  // Pagination state for assessments
  const [assessmentsPage, setAssessmentsPage] = useState(1);
  const [assessmentsPageSize, setAssessmentsPageSize] = useState(25);

  // Fetch supplier details
  const {
    data: supplierData,
    isLoading: supplierLoading,
    error: supplierError
  } = useQuery({
    queryKey: ['supplier', supplierId],
    queryFn: () => supplierService.getSupplier(supplierId),
    enabled: !!supplierId,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window regains focus
  });

  const supplier = supplierData?.supplier;

  // Fetch supplier contracts (server-side pagination, filtered by backend for CO/CM users)
  const { data: contractsData, isLoading: contractsLoading } = useQuery({
    queryKey: ['supplier-contracts', supplierId, contractsPage, contractsPageSize, contractsSortConfig],
    queryFn: () =>
      supplierService.getSupplierContracts(supplierId, {
        limit: contractsPageSize,
        offset: (contractsPage - 1) * contractsPageSize,
        sortBy: contractsSortConfig.field,
        order: contractsSortConfig.direction
      }),
    enabled: !!supplierId,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window regains focus
  });

  const contracts = contractsData?.contracts || [];
  const contractsTotal = contractsData?.total || 0; // For pagination (filtered for CO/CM)
  const contractsActualTotal = contractsData?.actualTotal || 0; // For statistics (unfiltered)
  const contractsTotalPages = Math.ceil(contractsTotal / contractsPageSize);

  // Fetch supplier assessments (server-side pagination, filtered by backend for CO/CM users and by supplier_id)
  const { data: assessmentsResponse, isLoading: assessmentsLoading } = useQuery({
    queryKey: ['supplier-assessments', supplierId, assessmentsPage, assessmentsPageSize],
    queryFn: () =>
      assessmentService.getAssessments({
        supplier_id: supplierId,
        limit: assessmentsPageSize,
        offset: (assessmentsPage - 1) * assessmentsPageSize
      }),
    enabled: !!supplierId,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window regains focus
  });

  const assessments = assessmentsResponse?.data || [];
  const assessmentsTotal = assessmentsResponse?.total || 0; // For pagination (filtered for CO/CM)
  const assessmentsActualTotal = assessmentsResponse?.actualTotal || 0; // For statistics (unfiltered)
  const assessmentsTotalPages = Math.ceil(assessmentsTotal / assessmentsPageSize);

  // Update supplier mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data, note }: { id: number; data: UpdateSupplierRequest; note: string }) => {
      const updated = await supplierService.updateSupplier(id, data);
      const trimmed = note.trim();
      if (trimmed) {
        try {
          await noteService.createNote('supplier', id, { content: trimmed });
        } catch (err) {
          // Best-effort: a failed note save must NOT fail supplier update
          console.error('Failed to save supplier note:', err);
        }
      }
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', supplierId] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['audit-history', 'supplier', supplierId] });
      queryClient.invalidateQueries({ queryKey: ['notes', 'supplier', supplierId] });
      // Invalidate assessment queries since tier changes may create/resolve conflicts
      queryClient.invalidateQueries({ queryKey: ['supplier-assessments', supplierId] });
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      queryClient.invalidateQueries({ queryKey: ['assessments-table'] });
      // Invalidate contract assessments in case they're cached
      queryClient.invalidateQueries({ queryKey: ['contract-assessments'] });
      setShowEditModal(false);
      showToast.success('Supplier updated successfully');
    },
    onError: (error: ApiError) => {
      console.error('Failed to update supplier:', error);
      // Error is passed to EditSupplierForm via error prop
    }
  });

  // Delete supplier mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => supplierService.deleteSupplier(id),
    onSuccess: async () => {
      // Remove from cache and refetch suppliers list to ensure fresh data
      queryClient.removeQueries({ queryKey: ['supplier', supplierId] });
      await queryClient.refetchQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      showToast.success('Supplier deleted successfully');
      navigate('/suppliers');
    },
    onError: (error: ApiError) => {
      console.error('Failed to delete supplier:', error);
      setShowDeleteModal(false);
      // Error toast handled by global error system
    }
  });

  const handleBack = () => {
    navigate(-1); // Go back to previous page in browser history
  };

  const handleViewContract = (contractId: number) => {
    navigate(`/contracts/${contractId}`);
  };

  const handleViewAssessment = (assessmentId: number) => {
    navigate(`/assessments/${assessmentId}`);
  };

  // Executive users can only access via dashboard widgets (unless they also have other roles)
  if (
    hasRole(user, ROLES.EXECUTIVE) &&
    !hasRole(user, [
      ROLES.ADMIN,
      ROLES.PROGRAM_LEAD,
      ROLES.SCHEDULER,
      ROLES.CONTRACT_ROLE,
      ROLES.ASSESSOR
    ]) &&
    !location.state?.fromWidget
  ) {
    return (
      <PageLayout>
        <ErrorState
          title="Access Restricted"
          message="Supplier details can only be accessed from dashboard widgets."
          action={
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          }
        />
      </PageLayout>
    );
  }

  // Handle errors
  if (supplierError) {
    const error = supplierError as ApiError;

    // Handle 404 Not Found
    if (error?.response?.status === 404) {
      return (
        <PageLayout>
          <ErrorState
            title="Supplier Not Found"
            message="The supplier you're looking for doesn't exist or has been deleted."
            action={
              <Button variant="primary" onClick={() => navigate('/suppliers')}>
                Go to Suppliers
              </Button>
            }
          />
        </PageLayout>
      );
    }

    // Handle 403 Forbidden for CO/CM users
    if (error?.response?.status === 403) {
      return (
        <PageLayout>
          <ErrorState
            title="Access Restricted"
            message="You can only view suppliers associated with your assigned contracts. Please navigate to this supplier from one of your contract pages."
            action={
              <Button variant="primary" onClick={() => navigate('/contracts')}>
                Go to My Contracts
              </Button>
            }
          />
        </PageLayout>
      );
    }

    // Handle other errors
    return (
      <PageLayout>
        <ErrorState
          title="Error"
          message="Error loading supplier details. Please try again."
          action={
            <Button variant="secondary" onClick={() => navigate('/suppliers')}>
              Go to Suppliers
            </Button>
          }
        />
      </PageLayout>
    );
  }

  if (supplierLoading) {
    return (
      <PageLayout>
        <LoadingState message="Loading supplier details..." />
      </PageLayout>
    );
  }

  if (!supplier) {
    return (
      <PageLayout>
        <ErrorState message="Supplier not found." />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <DetailPageHeader
        icon={<Building size={24} />}
        title={supplier.name}
        subtitle={supplier.vendor_id ? `Supplier Number: ${supplier.vendor_id}` : undefined}
        backLabel="Go Back"
        onBack={handleBack}
        actions={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Badge
              variant={(() => {
                if (supplier.status === 'Active') return 'success';
                if (supplier.status === 'Deleted') return 'error';
                return 'default';
              })()}
            >
              {getSupplierStatusLabel(supplier.status)}
            </Badge>

            {permissions.canEditSuppliers && (
              <Button variant="secondary" size="sm" onClick={() => setShowEditModal(true)}>
                <Edit size={16} />
                Edit
              </Button>
            )}

            {permissions.canDeleteSuppliers && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowDeleteModal(true)}
                disabled={contractsActualTotal > 0}
                title={
                  contractsActualTotal > 0 ? 'Cannot delete supplier with associated contracts' : 'Delete supplier'
                }
              >
                <Trash2 size={16} />
                Delete
              </Button>
            )}
          </div>
        }
      />

      <div className="detail-content">
        <div className={styles.detailMain}>
          <SupplierInfo
            supplier={supplier}
            contractsCount={contractsActualTotal}
            assessmentsCount={assessmentsActualTotal}
            hideAuditMetadata={permissions.isContractRole}
          />

          <SupplierContracts
            contracts={contracts}
            isLoading={contractsLoading}
            onViewContract={handleViewContract}
            sorting={{
              sortConfig: contractsSortConfig,
              onSort: handleContractsSort,
              isSorted: isContractsSorted
            }}
            pagination={{
              currentPage: contractsPage,
              totalPages: contractsTotalPages,
              totalItems: contractsTotal,
              pageSize: contractsPageSize,
              onPageChange: setContractsPage,
              onPageSizeChange: (newSize) => {
                setContractsPageSize(newSize);
                setContractsPage(1); // Reset to first page
              }
            }}
          />

          <SupplierAssessments
            assessments={assessments}
            isLoading={assessmentsLoading}
            onViewAssessment={handleViewAssessment}
            pagination={{
              currentPage: assessmentsPage,
              totalPages: assessmentsTotalPages,
              totalItems: assessmentsTotal,
              pageSize: assessmentsPageSize,
              onPageChange: setAssessmentsPage,
              onPageSizeChange: (newSize) => {
                setAssessmentsPageSize(newSize);
                setAssessmentsPage(1); // Reset to first page
              }
            }}
          />

          <AttachmentSection
            entityType="supplier"
            entityId={supplierId}
            canManage={permissions.isAdmin || permissions.isScheduler}
          />

          {permissions.canAccessNotes && (
            <EntityActivity entityType="supplier" entityId={supplierId} title="Supplier Activity" />
          )}
        </div>
      </div>

      {/* Edit Supplier Modal */}
      <GenericModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Supplier"
        size="md"
        disableBackdropClick
      >
        <EditSupplierForm
          supplier={supplier}
          contractsCount={contractsActualTotal}
          onSubmit={(data, note) => updateMutation.mutate({ id: supplierId, data, note })}
          onCancel={() => setShowEditModal(false)}
          isSubmitting={updateMutation.isPending}
          error={updateMutation.error || undefined}
        />
      </GenericModal>

      {/* Delete Supplier Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => deleteMutation.mutate(supplierId)}
        title="Delete Supplier"
        message={`Are you sure you want to delete "${supplier.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        isProcessing={deleteMutation.isPending}
      />
    </PageLayout>
  );
};

export default SupplierDetailPage;
